import React from 'react';
import { Form } from 'react-bootstrap';
import { Hourglass } from 'lucide-react';
import { CostUnit } from '../types/Enums';
import { LifetimeLibraryFields } from '../types/LibraryLifetimeTemplate';
import { LibraryFormData } from '../types/LibraryTemplate';
import { formatCostUnit } from '../Helpers/maintenanceCostUtils';
import {
    parseCsvCost,
    formatCsvCost,
    parseCsvCostUnit,
    formatCsvCostUnit,
    CSV_COST_UNIT_TOKENS,
} from '../Helpers/csvUtils';
import { LibraryConfig, COST_UNIT_OPTIONS, isCostUnit } from './LibraryConfig';
import NumberInput from '../Components/NumberInput';

/** Accepts exactly "30 år" or "30" (whole years, anchored). */
function parseCsvLifetimeYears(lifetime: string): number | null {
    const match = lifetime.match(/^\s*(\d+)\s*(?:år)?\s*$/i);
    if (!match) return null;
    const years = parseInt(match[1], 10);
    return years > 0 ? years : null;
}

type LifetimeForm = LibraryFormData<LifetimeLibraryFields>;

const LifetimeFields: React.FC<{
    form: LifetimeForm;
    setForm: React.Dispatch<React.SetStateAction<LifetimeForm>>;
    disabled: boolean;
}> = ({ form, setForm, disabled }) => (
    <>
        <div className="row g-3">
            <div className="col-md-6">
                <Form.Group>
                    <Form.Label>Förväntad livslängd (år)</Form.Label>
                    <NumberInput
                        className="form-control"
                        min="1"
                        step="1"
                        integer
                        value={form.expectedLifetimeYears}
                        onChange={(value) => setForm((prev) => ({ ...prev, expectedLifetimeYears: value }))}
                        placeholder="T.ex. 25"
                        disabled={disabled}
                    />
                </Form.Group>
            </div>
            <div className="col-md-6">
                <Form.Group>
                    <Form.Label>Kostnadsenhet</Form.Label>
                    <Form.Select
                        value={form.costUnit}
                        onChange={(e) => setForm((prev) => ({ ...prev, costUnit: e.target.value as CostUnit }))}
                        disabled={disabled}
                    >
                        {COST_UNIT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </div>
        </div>

        <Form.Group className="mt-3">
            <Form.Label>Ersättningskostnad ({formatCostUnit(form.costUnit)})</Form.Label>
            <NumberInput
                className="form-control"
                min="0"
                step="1"
                integer
                value={form.replacementCost}
                onChange={(value) => setForm((prev) => ({ ...prev, replacementCost: value }))}
                placeholder="T.ex. 2500"
                disabled={disabled}
            />
        </Form.Group>
    </>
);

/**
 * Lifetime & replacement cost library: templates that admins copy when
 * configuring expected lifetime for a component type.
 */
export const lifetimeLibrary: LibraryConfig<LifetimeLibraryFields> = {
    path: 'config/lifetimeLibraryTemplates',
    fieldKeys: ['expectedLifetimeYears', 'replacementCost', 'costUnit'],
    defaultFields: {
        expectedLifetimeYears: 30,
        replacementCost: 0,
        costUnit: 'per_sqm',
    },

    parseFields(d, key) {
        if (typeof d.expectedLifetimeYears !== 'number') {
            console.warn(`Invalid lifetime library template ${key}: missing expectedLifetimeYears`);
            return null;
        }
        if (typeof d.replacementCost !== 'number') {
            console.warn(`Invalid lifetime library template ${key}: missing replacementCost`);
            return null;
        }
        if (typeof d.costUnit !== 'string') {
            console.warn(`Invalid lifetime library template ${key}: missing costUnit`);
            return null;
        }
        return {
            expectedLifetimeYears: d.expectedLifetimeYears,
            replacementCost: d.replacementCost,
            costUnit: d.costUnit as CostUnit,
        };
    },

    validateFields(f, mode) {
        const check = <K extends keyof LifetimeLibraryFields>(key: K) => mode === 'full' || f[key] !== undefined;

        if (check('expectedLifetimeYears') && (f.expectedLifetimeYears === undefined || f.expectedLifetimeYears <= 0)) {
            throw new Error('Livslängd måste vara större än 0');
        }
        if (
            check('replacementCost') &&
            (f.replacementCost === undefined || !Number.isInteger(f.replacementCost) || f.replacementCost < 0)
        ) {
            throw new Error('Ersättningskostnad måste vara ett heltal, 0 eller större');
        }
        if (check('costUnit') && !isCostUnit(f.costUnit)) {
            throw new Error('Ogiltig kostnadsenhet');
        }
    },

    labels: {
        title: 'Livslängdsbibliotek',
        description:
            'Hantera mallar för livslängd och ersättningskostnad som användare kan kopiera när de konfigurerar komponenttyper',
        icon: Hourglass,
        loadError: 'Kunde inte ladda livslängdsbiblioteket',
        pickerTitle: 'Välj livslängd från bibliotek',
        addNamePlaceholder: 'T.ex. Betongtakpannor',
        deleteNote: 'Komponenttyper som redan kopierat värdena påverkas inte.',
        resetNoun: 'livslängdsbiblioteket',
        csvHelp:
            'CSV-filen ska ha kolumnerna: Namn, Livslängd (hela år, t.ex. "30 år"), Pris (heltal i kr, t.ex. "2500 kr"), ' +
            'Enhet (m2/st/m), Bastyper (kommaseparerade). Filer med fel i någon rad importeras inte.',
        exportFilePrefix: 'livslangdsbibliotek',
    },

    csv: {
        columns: ['Livslängd', 'Pris', 'Enhet'],
        parseColumns([lifetimeStr, priceStr, unit]) {
            const expectedLifetimeYears = parseCsvLifetimeYears(lifetimeStr);
            if (!expectedLifetimeYears) {
                return {
                    ok: false,
                    label: 'Ogiltig livslängd',
                    detail: lifetimeStr ? `Ogiltig livslängd: "${lifetimeStr}" (hela år, t.ex. "30 år")` : 'Tom livslängd',
                };
            }
            const replacementCost = parseCsvCost(priceStr);
            if (replacementCost === null) {
                return {
                    ok: false,
                    label: 'Ogiltig kostnad',
                    detail: `Ogiltig kostnad: "${priceStr}" (endast heltal i kr, t.ex. "2500 kr")`,
                };
            }
            const costUnit = parseCsvCostUnit(unit);
            if (!costUnit) {
                return {
                    ok: false,
                    label: 'Okänd enhet',
                    detail: `Okänd enhet: "${unit}" (endast ${CSV_COST_UNIT_TOKENS} stöds)`,
                };
            }
            return { ok: true, fields: { expectedLifetimeYears, replacementCost, costUnit } };
        },
        formatColumns(t) {
            return [`${t.expectedLifetimeYears} år`, formatCsvCost(t.replacementCost), formatCsvCostUnit(t.costUnit)];
        },
    },

    renderFields(form, setForm, disabled) {
        return <LifetimeFields form={form} setForm={setForm} disabled={disabled} />;
    },

    formatSummary(t) {
        return `${t.expectedLifetimeYears} år · ${t.replacementCost.toLocaleString('sv-SE')} ${formatCostUnit(t.costUnit)}`;
    },
};
